/**
 * Currency Types and Constants
 * Single source of truth for supported Bitfinex lending currencies
 */

/**
 * Supported currencies for Bitfinex lending
 * Based on Bitfinex API verification (2025-10-17)
 * See: specs/005-currency-configuration-fix/research.md
 */
export const SUPPORTED_CURRENCIES = ['USD', 'BTC', 'ETH', 'UST', 'TESTUSDT'] as const

/**
 * Currency type (union of supported currencies)
 */
export type Currency = typeof SUPPORTED_CURRENCIES[number]

/**
 * Currency display labels (with descriptions)
 */
export const CURRENCY_LABELS: Record<Currency, string> = {
  USD: 'USD (美元)',
  BTC: 'BTC (比特幣)',
  ETH: 'ETH (以太坊)',
  UST: 'UST (Terra)',
  TESTUSDT: 'TESTUSDT (測試網)',
}

/**
 * Currency funding symbols (for Bitfinex API calls)
 */
export const CURRENCY_FUNDING_SYMBOLS: Record<Currency, string> = {
  USD: 'fUSD',
  BTC: 'fBTC',
  ETH: 'fETH',
  UST: 'fUST',
  TESTUSDT: 'fTESTUSDT',
}

/**
 * Currency metadata
 */
export interface CurrencyMetadata {
  symbol: string
  label: string
  fundingSymbol: string
  liquidity: 'very_high' | 'high' | 'medium' | 'low'
  isTestnet: boolean
}

export const CURRENCY_METADATA: Record<Currency, CurrencyMetadata> = {
  USD: {
    symbol: 'USD',
    label: 'USD (美元)',
    fundingSymbol: 'fUSD',
    liquidity: 'very_high',
    isTestnet: false,
  },
  BTC: {
    symbol: 'BTC',
    label: 'BTC (比特幣)',
    fundingSymbol: 'fBTC',
    liquidity: 'medium',
    isTestnet: false,
  },
  ETH: {
    symbol: 'ETH',
    label: 'ETH (以太坊)',
    fundingSymbol: 'fETH',
    liquidity: 'medium',
    isTestnet: false,
  },
  UST: {
    symbol: 'UST',
    label: 'UST (Terra)',
    fundingSymbol: 'fUST',
    liquidity: 'very_high',
    isTestnet: false,
  },
  TESTUSDT: {
    symbol: 'TESTUSDT',
    label: 'TESTUSDT (測試網)',
    fundingSymbol: 'fTESTUSDT',
    liquidity: 'very_high',
    isTestnet: true,
  },
}

/**
 * Type guard to check if a string is a valid currency
 */
export function isCurrency(value: string): value is Currency {
  return SUPPORTED_CURRENCIES.includes(value as Currency)
}

/**
 * Get currency label by symbol
 */
export function getCurrencyLabel(currency: Currency): string {
  return CURRENCY_LABELS[currency]
}

/**
 * Get funding symbol for Bitfinex API
 */
export function getFundingSymbol(currency: Currency): string {
  return CURRENCY_FUNDING_SYMBOLS[currency]
}

/**
 * Get currency metadata
 */
export function getCurrencyMetadata(currency: Currency): CurrencyMetadata {
  return CURRENCY_METADATA[currency]
}
